import React, { ChangeEvent, FC, useState } from 'react'
import { gql, useMutation } from '@apollo/client'
import { BusyIndicator } from '@/atoms/BusyIndicator/BusyIndicator'
import { Button } from '@/atoms/Button'
import { CheckBox } from '@/atoms/CheckBox/CheckBox'
import { TextArea } from '@/atoms/Input'
import { Translate, useTranslate } from '@/utils/translate/translate-client'

interface SendThankYouResponse {
  data: {
    thankYouNote: string
  }
}

export const SAVE_THANK_YOU_MUTATION = gql`
  mutation thankYouMutation($input: ThankYouInput!) {
    thankYouMutation(input: $input) {
      thankYouNote
    }
  }
`

interface SayThankYouFormProps {
  projectSlug: string
  onSave: () => void
  onCancel: () => void
}

export const SayThankYouForm: FC<SayThankYouFormProps> = ({ onSave, onCancel, projectSlug }) => {
  const { t } = useTranslate('watch')
  const [thankYou, setThankYou] = useState('')
  const [acceptPublicNote, setAcceptPublicNote] = useState(true)

  const [thankYouMutation, { data: sendThankYouResponse, loading: sendThankYouLoading }] =
    useMutation<SendThankYouResponse>(SAVE_THANK_YOU_MUTATION, {
      errorPolicy: 'all',
    })

  const handleShareClickButton = async () => {
    await thankYouMutation({ variables: { input: { projectSlug, thankYouNote: thankYou } } })
    onSave()
  }

  const handleOnClose = () => {
    onCancel()
  }

  return (
    <div>
      <label className="text-sm">Message*</label>
      <TextArea
        disabled={sendThankYouLoading}
        rows={12}
        value={thankYou}
        label=""
        onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setThankYou(e.target.value)}
      />
      <div>{sendThankYouResponse?.data?.thankYouNote}</div>
      <CheckBox
        disabled={sendThankYouLoading}
        checked={acceptPublicNote}
        onChange={(e: ChangeEvent<HTMLInputElement>) => setAcceptPublicNote(e.target.checked)}
      >
        <Translate t={t} i18nKey="acceptNoteMayAppearPublicly">
          I understand my note may appear publicly
        </Translate>
      </CheckBox>
      <div className="mt-4 flex justify-center space-x-4 md:justify-end">
        <Button onClick={handleOnClose} className="text-sm text-black" variant="gray" outline>
          <Translate t={t} i18nKey="cancel">
            Cancel
          </Translate>
        </Button>
        <Button
          variant="offblack"
          className="text-sm"
          disabled={thankYou.length === 0 || !acceptPublicNote || sendThankYouLoading}
          onClick={handleShareClickButton}
        >
          <Translate t={t} i18nKey="sayThankYouClickButton">
            Say Thank You
          </Translate>
          <BusyIndicator showBusy={sendThankYouLoading} />
        </Button>
      </div>
    </div>
  )
}
